import { useCallback, useEffect, useMemo, useState } from "react";

import {
  PointOfSale as PointOfSaleIcon,
  ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import { Alert, Box, CircularProgress, Paper } from "@mui/material";
import { PageContainer, useSnackbar } from "@/features/ui";
import { apiClient } from "@/features/shared";

import {
  AccountingActionCategory,
  ActionSectionMapping,
} from "../features/chart-of-account-preferences";
import { PreferenceTab } from "../features/chart-of-account-preferences/preference-tab";

// Read-only default (contextId=DEFAULT) account mappings. These are the hidden
// fallback accounts an entity (customer/supplier/outlet) uses when it has no
// entity-specific AACOA override. Action lists are reused from the customer /
// supplier section mappings so they stay in sync with a single source of truth.
const DEFAULT_ACCOUNT_SECTIONS = [
  {
    title: "Akun Penjualan",
    icon: <PointOfSaleIcon />,
    actions: ActionSectionMapping[AccountingActionCategory.CUSTOMER][0].actions,
  },
  {
    title: "Akun Pembelian",
    icon: <ShoppingCartIcon />,
    actions: ActionSectionMapping[AccountingActionCategory.SUPPLIER][0].actions,
  },
];

const noop = () => {};

export const DefaultAccounts = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState({});

  const fetchDefaultMappings = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await apiClient.get(
        "accounting-action-chart-of-accounts",
        {
          params: {
            contextId: "DEFAULT",
            withChartOfAccount: true,
          },
        },
      );

      // Group by action, collect full COA objects (same shape PreferenceTab
      // expects: { [action]: ChartOfAccount[] }).
      const mappingsMap = {};
      (data.data || []).forEach((item) => {
        if (!item.chartOfAccount) return;
        if (!mappingsMap[item.action]) {
          mappingsMap[item.action] = [];
        }
        mappingsMap[item.action].push(item.chartOfAccount);
      });

      setMappings(mappingsMap);
    } catch (error) {
      showSnackbar("Gagal memuat akun default", "error");
      console.error("Error fetching default account mappings:", error);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchDefaultMappings();
  }, [fetchDefaultMappings]);

  const sections = useMemo(() => DEFAULT_ACCOUNT_SECTIONS, []);

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Akun default yang otomatis digunakan pada transaksi ketika pelanggan,
          pemasok, atau cabang belum memiliki pemetaan akun sendiri. Halaman ini
          bersifat read-only.
        </Alert>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minHeight: 300,
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <PreferenceTab
            sections={sections}
            preferences={mappings}
            onUpdatePreferences={noop}
            readOnly
          />
        )}
      </Paper>
    </PageContainer>
  );
};
